import Cookies from "js-cookie";
import {updateUserProperties} from "./amplitude";

export const TEST_CONTAINER = "ContentfulTest";
const TEST_COOKIE_NAME = "splitTest";

export interface TestContainer {
  __typename: string;
  testComponents: Array<TestComponent>;
  testName: string;
}

interface TestComponent {
  __typename: "ContentfulTestComponent";
  distributionPercentage: number;
  testGroup: string;
  component: Record<any, any>;
}

/**
 * method to get test cookies data
 * @return {string}
 */
export function getTestCookiesData(): string {
  if (Cookies.get(TEST_COOKIE_NAME) === "[object Object]") {
    Cookies.set(TEST_COOKIE_NAME, "{}");
  }

  const cookieValue = Cookies.get(TEST_COOKIE_NAME);
  const abTestCookies = cookieValue ? JSON.parse(cookieValue) : {};
  const groupList = Object.keys(abTestCookies).map(
    testName => `${testName}_${abTestCookies[testName]}`
  );

  return groupList.toString();
}

/**
 *
 * @param {TestContainer} test
 * @return {any}
 */
export function pickTestComponent(test: TestContainer): any {
  if (!test || !test.testComponents.length) return null;

  const testGroupFromCookie = getTestCookie(test);
  const alreadyEnrolledInTest = testGroupFromCookie !== undefined;

  if (alreadyEnrolledInTest) {
    const testComponent = test.testComponents.find(
      component => component.testGroup === testGroupFromCookie
    );

    if (!testComponent) {
      clearTestCookie(test);
    } else {
      return testComponent;
    }
  }

  // ensure probabilities sum to 100; necessary to guarante random int belongs to one testComponent
  const validatedTest = validatePercentages(test);

  const randomIntBetween1and100 = Math.floor(Math.random() * 100 + 1);

  let selectedTestGroup: any;
  // run through the components until we have found the one where the random number is within the distribution
  validatedTest.testComponents.reduce(
    (processedDistribution, testComponent) => {
      if (selectedTestGroup) return processedDistribution;
      const newProcessedDistribution =
        processedDistribution + testComponent.distributionPercentage;
      if (newProcessedDistribution >= randomIntBetween1and100) {
        selectedTestGroup = testComponent.testGroup;
      }

      return newProcessedDistribution;
    },
    0
  );

  const testComponent = test.testComponents.find(
    component => component.testGroup === selectedTestGroup
  );

  if (testComponent && testComponent.testGroup) {
    setTestCookie(test, testComponent.testGroup);
    setAmplitudeTracking(test, testComponent.testGroup);
    return testComponent;
  }
  return null;
}

/**
 *
 * @param {TestContainer} test
 * @return {TestContainer}
 */
const validatePercentages = (test: TestContainer): TestContainer => {
  // check if testComponents' distribution percentages sum to 100
  const probDiff =
    100 -
    test.testComponents.reduce((sum, el) => sum + el.distributionPercentage, 0);

  // if they don't, distribute difference (pos or neg) equally to all testComponents
  // if any remainder due to rounding, add it to first component in list
  if (probDiff) {
    const diffChunk = probDiff / test.testComponents.length;
    const incInt = Math.sign(diffChunk) * Math.round(Math.abs(diffChunk)); // Math.floor, but also works for negatives; -2.1 => -2
    const remainder = probDiff - incInt * test.testComponents.length;

    test.testComponents.forEach(component => {
      component.distributionPercentage += incInt;
    });
    if (remainder) {
      test.testComponents[0].distributionPercentage += remainder;
    }
  }

  return test;
};

/**
 *
 * @param {TestContainer} test
 * @return {string}
 */
const getTestCookie = (test: TestContainer): string => {
  if (Cookies.get(TEST_COOKIE_NAME) === "[object Object]") {
    Cookies.set(TEST_COOKIE_NAME, "{}");
  }

  const {testName} = test;
  const cookieValue = Cookies.get(TEST_COOKIE_NAME);
  const testCookies = cookieValue ? JSON.parse(cookieValue) : {};
  return testCookies[testName]!;
};

/**
 *
 * @param {TestContainer} test
 * @param {string} group
 */
const setTestCookie = (test: TestContainer, group: string): void => {
  if (Cookies.get(TEST_COOKIE_NAME) === "[object Object]") {
    Cookies.set(TEST_COOKIE_NAME, "{}");
  }

  const {testName} = test;
  const cookieValue = Cookies.get(TEST_COOKIE_NAME);
  const testCookies = cookieValue ? JSON.parse(cookieValue) : {};
  testCookies[testName] = group;
  Cookies.set(TEST_COOKIE_NAME, JSON.stringify(testCookies));
};

/**
 *
 * @param {TestContainer} test
 */
const clearTestCookie = (test: TestContainer): void => {
  if (Cookies.get(TEST_COOKIE_NAME) === "[object Object]") {
    Cookies.set(TEST_COOKIE_NAME, "{}");
  }

  const {testName} = test;
  const cookieValue = Cookies.get(TEST_COOKIE_NAME);
  const testCookies = cookieValue ? JSON.parse(cookieValue) : {};
  testCookies[testName] = undefined;
  Cookies.set(TEST_COOKIE_NAME, JSON.stringify(testCookies));
};

/**
 *
 * @param {TestContainer} test
 * @param {string} group
 */
const setAmplitudeTracking = (test: TestContainer, group: string): void => {
  if (Cookies.get(TEST_COOKIE_NAME) === "[object Object]") {
    Cookies.set(TEST_COOKIE_NAME, "{}");
  }

  const cookieValue = Cookies.get(TEST_COOKIE_NAME);
  const testCookies = cookieValue ? JSON.parse(cookieValue) : {};

  const ampValues = Object.keys(testCookies).map(
    testName => `${testName}_${group}`
  );

  updateUserProperties({splitTest: ampValues});
};
